import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecords, ago } from '../ui.js';
import { sendEmail, createRecord, ApiError } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// THE STREAM — threaded, selectable, and it tells you who has gone quiet.
//
// V1 merged emails, notes and activities into one chronological list. That
// killed the four-tab filing cabinet, and Kyle's read was right: the merge was
// the strong part. But pointed at a real conversation it fell over.
//
//   Seeded a realistic account — a five-message back-and-forth with Riley over
//   eighteen days, plus one message to Thomas. V1 renders that as SIX ROWS,
//   five of which say "Re: Payra + Hamer". The story is unreadable precisely
//   where there is finally a story to read.
//
// V2 — group on thread_key. Two rows instead of six, latest first, expandable.
// Sketching it surfaced a bug I had written myself: the composer sets
// `thread_key = contact:<id>`, so EVERY EMAIL TO A PERSON IS ONE THREAD FOREVER.
// A proposal conversation in March and a re-engagement in September collapse
// into a single row with "23 messages" on it, which is worse than not threading.
//
// V3 — thread on the key, then BREAK THE THREAD ON SILENCE. More than 30 days
// between messages is a new conversation, because that is what a human means by
// one. It also matches how a reply reads: nobody experiences a September email
// as a continuation of March.
//
// SELECTION. A stream you can only read is a log. A stream you can select from
// is a working surface — reply in context, pin the moment they said "we have
// budget" to the deal, and quote them back to themselves, which is the single
// highest-value thing a rep does and no CRM makes easy.
//
// THE QUIET STRIP. Multi-threading fails silently: you touch the champion four
// times and the economic buyer once, and nothing tells you until the deal
// stalls. Per-contact days-since-touch, at the top, where it cannot be missed.
// ─────────────────────────────────────────────────────────────────────────────

/** A gap this long is a new conversation, not a continuation. */
const THREAD_BREAK_DAYS = 30;
/** Past this, a contact on an active account has gone quiet. */
const QUIET_DAYS = 14;

type Msg = {
  id: string; at: string; inbound: boolean; status: string;
  subject: string; body: string; who: string; contactId: string | null;
  opened: boolean;
};
type Item =
  | { kind: 'thread'; id: string; at: string; subject: string; who: string;
      contactId: string | null; msgs: Msg[]; unanswered: boolean }
  | { kind: 'event'; id: string; at: string; type: string; title: string;
      body: string | null; meta: string | null; contactId: string | null };

const ICON: Record<string, string> = {
  thread: '✉', note: '✎', call: '☏', meeting: '◷', linkedin: 'in',
  email: '✉', research: '⌕', other: '•',
};

export function Stream({ companyId, contacts, onCompose, onReply, onLogged, hideComposer }: {
  companyId: string;
  contacts: { id: string; name: string; email?: string | null }[];
  onCompose: () => void;
  onReply?: (m: { contactId: string | null; subject: string; quote: string }) => void;
  onLogged?: () => void;
  /** The console above the record composes now; two boxes is one too many. */
  hideComposer?: boolean;
}) {
  const emails = useRecords<Record<string, unknown> & { id: string }>('emails', { company_id: companyId });
  const acts = useRecords<Record<string, unknown> & { id: string }>('activities', { company_id: companyId });
  const deals = useRecords<{ id: string; name: string }>('deals', { company_id: companyId });

  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [sel, setSel] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'email' | 'note'>('all');
  const listRef = useRef<HTMLOListElement>(null);

  // --- build the feed -------------------------------------------------------
  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];

    // Emails first, grouped by key then split on silence.
    const byKey = new Map<string, Msg[]>();
    for (const e of emails.rows ?? []) {
      const m: Msg = {
        id: String(e.id),
        at: String(e.sent_at ?? e.received_at ?? e.created_at),
        inbound: e.direction === 'inbound',
        status: String(e.status ?? ''),
        subject: String(e.subject ?? '(no subject)'),
        body: String(e.body ?? ''),
        // An inbound message's `to_email` is OUR address. Labelling a reply
        // with the address it arrived at reads as though we wrote it.
        who: e.direction === 'inbound'
          ? String(e.from_name ?? e.from_email ?? '')
          : String(e.to_name ?? e.to_email ?? ''),
        contactId: e.contact_id ? String(e.contact_id) : null,
        opened: Boolean(e.opened_at),
      };
      const k = String(e.thread_key ?? `solo:${m.id}`);
      if (!byKey.has(k)) byKey.set(k, []);
      byKey.get(k)!.push(m);
    }

    for (const [k, all] of byKey) {
      all.sort((a, b) => +new Date(a.at) - +new Date(b.at));
      // THE SILENCE BREAK. Walk forward; a gap over the threshold starts a new
      // conversation. Without this, thread_key = contact:<id> welds a year of
      // unrelated exchanges into one row.
      let run: Msg[] = [];
      const flush = () => {
        if (!run.length) return;
        const last = run[run.length - 1];
        out.push({
          kind: 'thread', id: `${k}:${run[0].id}`, at: last.at,
          subject: run[0].subject.replace(/^re:\s*/i, ''),
          who: last.who || run[0].who, contactId: last.contactId,
          msgs: [...run].reverse(),
          // Their message is the newest one: the ball is in your court.
          unanswered: last.inbound,
        });
        run = [];
      };
      for (const m of all) {
        if (run.length) {
          const gap = (+new Date(m.at) - +new Date(run[run.length - 1].at)) / 86_400_000;
          if (gap > THREAD_BREAK_DAYS) flush();
        }
        run.push(m);
      }
      flush();
    }

    for (const a of acts.rows ?? []) {
      out.push({
        kind: 'event', id: String(a.id), at: String(a.occurred_at),
        type: String(a.kind ?? 'other'), title: String(a.summary),
        body: a.detail ? String(a.detail) : null,
        meta: String(a.category ?? ''),
        contactId: a.contact_id ? String(a.contact_id) : null,
      });
    }

    out.sort((x, y) => +new Date(y.at) - +new Date(x.at));
    if (filter === 'email') return out.filter((i) => i.kind === 'thread');
    if (filter === 'note') return out.filter((i) => i.kind === 'event');
    return out;
  }, [emails.rows, acts.rows, filter]);

  // --- who has gone quiet ---------------------------------------------------
  const quiet = useMemo(() => {
    const last = new Map<string, string>();
    const bump = (id: string | null, at: string) => {
      if (!id) return;
      if (!last.has(id) || +new Date(at) > +new Date(last.get(id)!)) last.set(id, at);
    };
    for (const e of emails.rows ?? []) {
      bump(e.contact_id ? String(e.contact_id) : null,
        String(e.sent_at ?? e.received_at ?? e.created_at));
    }
    for (const a of acts.rows ?? []) {
      bump(a.contact_id ? String(a.contact_id) : null, String(a.occurred_at));
    }
    return contacts.map((c) => {
      const at = last.get(c.id) ?? null;
      const days = at ? Math.floor((Date.now() - +new Date(at)) / 86_400_000) : null;
      return { ...c, days, touched: at !== null };
    }).sort((a, b) => (b.days ?? 9999) - (a.days ?? 9999));
  }, [emails.rows, acts.rows, contacts]);

  // --- keyboard -------------------------------------------------------------
  // A rep who lives here all day should not need the mouse to read a record.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const t = ev.target as HTMLElement;
      if (t.tagName === 'TEXTAREA' || t.tagName === 'INPUT') return;
      const idx = items.findIndex((i) => i.id === sel);
      if (ev.key === 'j') { ev.preventDefault(); setSel(items[Math.min(items.length - 1, idx + 1)]?.id ?? items[0]?.id ?? null); }
      if (ev.key === 'k') { ev.preventDefault(); setSel(items[Math.max(0, idx - 1)]?.id ?? null); }
      if (ev.key === 'Enter' && sel) {
        ev.preventDefault();
        setOpen((s) => { const n = new Set(s); n.has(sel) ? n.delete(sel) : n.add(sel); return n; });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, sel]);

  async function addNote() {
    const t = note.trim();
    if (!t) return;
    setSaving(true);
    try {
      const [first, ...rest] = t.split('\n');
      await acts.create({
        company_id: companyId, kind: 'note', category: 'sales',
        summary: first.slice(0, 200), detail: rest.join('\n') || null,
        occurred_at: new Date().toISOString(),
      });
      setNote(''); onLogged?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setSaving(false); }
  }

  /** Pin a moment to a deal. "We have budget" is evidence, not history. */
  async function pin(title: string, detail: string, contactId: string | null) {
    const d = deals.rows?.[0];
    if (!d) { setError('No deal on this account to pin to yet.'); return; }
    setBusy('pin'); setError(null);
    try {
      await createRecord('activities', {
        company_id: companyId, contact_id: contactId, deal_id: d.id,
        kind: 'note', category: 'sales',
        summary: `📌 ${title.slice(0, 180)}`, detail,
        occurred_at: new Date().toISOString(),
      });
      setMsg(`Pinned to ${d.name}.`);
      void acts.reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(null); }
  }

  return (
    <div className="stream">
      {/* Multi-threading fails silently. This is the only place it shows. */}
      {quiet.length > 0 && (
        <div className="quietstrip">
          {quiet.map((c) => (
            <span key={c.id} className={`qchip ${
              !c.touched ? 'never' : (c.days ?? 0) > QUIET_DAYS ? 'stale' : ''}`}>
              {c.name.split(' ')[0]}
              <em>{!c.touched ? 'never touched' : c.days === 0 ? 'today' : `${c.days}d`}</em>
            </span>
          ))}
        </div>
      )}

      {!hideComposer && <div className="composer-strip">
        <textarea rows={note ? 4 : 2} value={note}
          placeholder="What happened? Type a note, or use Email to write to someone."
          onChange={(e) => setNote(e.target.value)} />
        <div className="composer-strip-actions">
          <button className="primary" disabled={saving || !note.trim()}
            onClick={() => void addNote()}>{saving ? 'Saving…' : 'Log note'}</button>
          <button onClick={onCompose} disabled={contacts.length === 0}>Email</button>
          <div className="streamfilter">
            {(['all', 'email', 'note'] as const).map((f) => (
              <button key={f} className={filter === f ? 'on' : ''}
                onClick={() => setFilter(f)}>{f}</button>
            ))}
          </div>
        </div>
      </div>}

      {error && <p className="error">{error}</p>}
      {msg && <p className="ok-note">{msg}</p>}

      {items.length === 0 && (
        <p className="meta streamempty">
          Nothing has happened yet. Everything you send, log, or decide shows here in order.
        </p>
      )}

      <ol className="streamlist" ref={listRef}>
        {items.map((i) => {
          const isOpen = open.has(i.id);
          const isSel = sel === i.id;
          return (
            <li key={i.id}
              className={`sitem ${isSel ? 'sel' : ''} ${
                i.kind === 'thread' && i.unanswered ? 's-hot' : ''}`}
              onClick={() => setSel(i.id)}
            >
              <span className="sicon" aria-hidden="true">
                {i.kind === 'thread' ? ICON.thread : ICON[i.type] ?? '•'}
              </span>
              <div className="sbody">
                <div className="sline">
                  <strong>{i.kind === 'thread' ? i.subject : i.title}</strong>
                  {i.kind === 'thread' && i.msgs.length > 1 && (
                    <span className="tcount">{i.msgs.length}</span>
                  )}
                  {i.kind === 'thread' && i.unanswered && (
                    <span className="flag flag-warn">needs a reply</span>
                  )}
                  <span className="swhen">{ago(i.at)}</span>
                </div>
                <span className="smeta">
                  {i.kind === 'thread' ? i.who : i.meta}
                </span>

                {/* Collapsed: the newest message only. That is what you need to
                    know; the history is one click away and usually not needed. */}
                {i.kind === 'thread' && !isOpen && i.msgs[0]?.body && (
                  <p className="stext">{i.msgs[0].body.slice(0, 300)}</p>
                )}
                {i.kind === 'event' && i.body && !isOpen && (
                  <p className="stext">{i.body.slice(0, 300)}</p>
                )}
                {i.kind === 'event' && i.body && isOpen && (
                  <p className="stext">{i.body}</p>
                )}

                {i.kind === 'thread' && isOpen && (
                  <div className="msgs">
                    {i.msgs.map((m) => (
                      <div key={m.id} className={`msg ${m.inbound ? 'in' : 'out'}`}>
                        <div className="msghead">
                          <strong>{m.inbound ? m.who : 'You'}</strong>
                          <span className="meta">{ago(m.at)}</span>
                          {m.opened && <span className="flag flag-ok">opened</span>}
                          {m.status === 'scheduled' && (
                            <button className="mini" disabled={busy === m.id}
                              onClick={async (e) => {
                                e.stopPropagation(); setBusy(m.id); setError(null);
                                try {
                                  const r = await sendEmail(m.id);
                                  // /api/send is 200 even when it refuses:
                                  // failed rides in the body, and sent:0 with
                                  // no failure means the engine deferred (cap,
                                  // window, gate). Both were silent here.
                                  if (r.failed) setError(r.results?.[0]?.error ?? 'Send failed.');
                                  else if (r.sent === 0) setError('Deferred — a cap, the sending window, or a gate. It stays queued.');
                                  void emails.reload();
                                }
                                catch (err) { setError(err instanceof ApiError ? err.message : String(err)); }
                                finally { setBusy(null); }
                              }}>{busy === m.id ? '…' : 'Send'}</button>
                          )}
                        </div>
                        <p className="msgbody">{m.body}</p>
                        <div className="msgacts">
                          {onReply && (
                            <button className="mini" onClick={(e) => {
                              e.stopPropagation();
                              // Quote-back: the highest-value rep move, and the
                              // reason selection exists at all.
                              onReply({ contactId: m.contactId,
                                subject: `Re: ${i.subject}`,
                                quote: m.body.slice(0, 600) });
                            }}>Reply</button>
                          )}
                          <button className="mini" disabled={busy === 'pin'}
                            onClick={(e) => { e.stopPropagation();
                              void pin(m.subject, m.body, m.contactId); }}>
                            Pin to deal
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {(i.kind === 'thread' || i.body) && (
                <button className="mini sexpand" onClick={(e) => {
                  e.stopPropagation();
                  setOpen((s) => { const n = new Set(s); n.has(i.id) ? n.delete(i.id) : n.add(i.id); return n; });
                }}>{isOpen ? 'Close' : i.kind === 'thread' ? 'Open' : 'More'}</button>
              )}
            </li>
          );
        })}
      </ol>

      {items.length > 0 && (
        <p className="meta streamhint">j / k to move · Enter to open</p>
      )}
    </div>
  );
}
